/** The always-visible sticky footer: a note area, an optional secondary
 * button (Save draft), an optional extra button (the editor's second
 * render action) and a primary button (Render).
 *
 * Attributes: primary-label, primary-disabled ("true"), primary-busy
 * ("true"), primary-hint, secondary-label, extra-label, extra-disabled
 * ("true"), note.
 * Events (bubbling, composed): "footer-primary", "footer-secondary",
 * "footer-extra".
 */

export class WorkshopFooterBar extends HTMLElement {
  static observedAttributes = [
    "primary-label",
    "primary-disabled",
    "primary-busy",
    "primary-hint",
    "secondary-label",
    "extra-label",
    "extra-disabled",
    "note",
  ];

  #built = false;
  #note!: HTMLElement;
  #hint!: HTMLElement;
  #primary!: HTMLButtonElement;
  #secondary!: HTMLButtonElement;
  #extra!: HTMLButtonElement;

  connectedCallback(): void {
    if (!this.#built) this.#build();
    this.#sync();
  }

  attributeChangedCallback(): void {
    if (this.#built) this.#sync();
  }

  #build(): void {
    this.#built = true;
    this.innerHTML = `
      <div class="footer-bar">
        <div class="footer-hint"></div>
        <div class="footer-row">
          <span class="footer-note"></span>
          <span class="footer-actions">
            <button type="button" class="ss-btn ss-btn--secondary footer-secondary">
            </button>
            <button type="button" class="ss-btn ss-btn--secondary footer-extra">
            </button>
            <button type="button" class="ss-btn ss-btn--primary footer-primary">
            </button>
          </span>
        </div>
      </div>
    `;
    this.#note = this.querySelector(".footer-note") as HTMLElement;
    this.#hint = this.querySelector(".footer-hint") as HTMLElement;
    this.#primary = this.querySelector(
      ".footer-primary",
    ) as HTMLButtonElement;
    this.#secondary = this.querySelector(
      ".footer-secondary",
    ) as HTMLButtonElement;
    this.#extra = this.querySelector(".footer-extra") as HTMLButtonElement;

    this.#extra.addEventListener("click", () => {
      if (this.#extra.disabled) return;
      this.dispatchEvent(
        new CustomEvent("footer-extra", {
          bubbles: true,
          composed: true,
        }),
      );
    });
    this.#primary.addEventListener("click", () => {
      if (this.#primary.disabled) return;
      this.dispatchEvent(
        new CustomEvent("footer-primary", {
          bubbles: true,
          composed: true,
        }),
      );
    });
    this.#secondary.addEventListener("click", () => {
      this.dispatchEvent(
        new CustomEvent("footer-secondary", {
          bubbles: true,
          composed: true,
        }),
      );
    });
  }

  #sync(): void {
    const label = this.getAttribute("primary-label") ?? "";
    const busy = this.getAttribute("primary-busy") === "true";
    const disabled =
      this.getAttribute("primary-disabled") === "true" || busy;
    const hint = this.getAttribute("primary-hint") ?? "";
    const secondary = this.getAttribute("secondary-label") ?? "";
    const note = this.getAttribute("note") ?? "";

    this.#primary.textContent = label;
    this.#primary.disabled = disabled;
    this.#primary.classList.toggle("is-busy", busy);
    this.#primary.style.display = label ? "" : "none";

    this.#secondary.textContent = secondary;
    this.#secondary.style.display = secondary ? "" : "none";

    const extra = this.getAttribute("extra-label") ?? "";
    this.#extra.textContent = extra;
    this.#extra.style.display = extra ? "" : "none";
    this.#extra.disabled =
      this.getAttribute("extra-disabled") === "true" || busy;

    this.#hint.textContent = hint;
    this.#hint.style.display = hint ? "" : "none";

    this.#note.textContent = note;
  }
}

if (!customElements.get("scapescene-footer-bar")) {
  customElements.define("scapescene-footer-bar", WorkshopFooterBar);
}
